import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { chain, sortBy, uniqueId } from 'lodash';
import { toast } from 'react-toastify';

import CategoryTabs from '../../components/RestaurantMenu/CategoryTabs';
import SubCategories from '../../components/RestaurantMenu/SubCategories';
import MenuGrid from '../../components/RestaurantMenu/MenuGrid';
import ValidationErrorModal from '../../components/RestaurantMenu/ValidationErrorModal';
import Loader from '../../components/Loader';
import { Modal } from '../../components/ui/Modal';
import { MultiCounter } from '../../components/ui/MultiCounter';
import { Counter } from '../../components/ui/Counter';
import { RadioGroup } from '../../components/ui/RadioGroup';

import configData from '../../config.json';
import { isCategoryProperlyAvailable } from '../../utils/restaurant-availability';
import { renderCurrencyLanguage, hexToRgbString } from '../../utils';
import { useDataContext } from '../../context/data';
import { useOrderContext } from '../../context/order';
import { useRestaurantContext } from '../../context/restaurants';
import translations from '../../constants/translations.json';
import { useScrollSpy } from '../../hooks/useScrollSpy';
import GogiLanding from '../../components/GogiLanding';
import MaidoLanding from '../../components/MaidoLanding';

import './styles.css';
import ToggleNav from '../../components/ToggleNav';
import { guestFacingError } from '../../utils/serverError';
import OrderingPausedScreen from '../../components/OrderingPausedScreen';

const GOGI_RESTAURANT_ID = '69bd59b55a10b5001b234d1d';
const MAIDO_RESTAURANT_ID = '6a8d91b1da01c500160ea8aa';

const DEFAULT_IMG =
  'https://res.cloudinary.com/burgerkingnorway/image/upload/v1674745616/Heywaitr/1674745616-images.png';

// ─── Translation helpers (same pattern as RestaurantKiosk) ────────────────────

function getCategoriesTranslation(data, lang, restaurantLanguage) {
  return data.map((category) => {
    let l = lang;
    if (
      Object.keys(category?.Translation || {}).length === 1 &&
      Object.keys(category?.Translation || {}).includes(restaurantLanguage)
    )
      l = restaurantLanguage;
    if (!Object.keys(category?.Translation || {}).includes(l))
      l = Object.keys(category?.Translation || {})[0];
    return {
      ...category,
      Category:
        Object.keys(category?.Translation || {}).length > 0
          ? category.Translation?.[l]?.Category || ''
          : category?.Category || '',
      Description:
        Object.keys(category?.Translation || {}).length > 0
          ? category.Translation?.[l]?.Description || ''
          : category?.Description || '',
    };
  });
}

function getSubCategoriesTranslation(data, lang, restaurantLanguage) {
  return data.map((sub) => {
    let l = lang;
    if (
      Object.keys(sub?.Translation || {}).length === 1 &&
      Object.keys(sub?.Translation || {}).includes(restaurantLanguage)
    )
      l = restaurantLanguage;
    if (!Object.keys(sub?.Translation || {}).includes(l))
      l = Object.keys(sub?.Translation || {})[0];
    return {
      ...sub,
      Category:
        Object.keys(sub?.Translation || {}).length > 0
          ? sub.Translation?.[l]?.Category || ''
          : sub?.Category || '',
    };
  });
}

function getMenuTranslation(data, data2, lang, restaurantLanguage) {
  const translatedSubs = getSubCategoriesTranslation(data2, lang, restaurantLanguage);
  const translatedItems = data.map((item) => {
    let l = lang;
    if (
      Object.keys(item?.Translation || {}).length === 1 &&
      Object.keys(item?.Translation || {}).includes(restaurantLanguage)
    )
      l = restaurantLanguage;
    if (!Object.keys(item?.Translation || {}).includes(l))
      l = Object.keys(item?.Translation || {})[0];
    return {
      ...item,
      name:
        Object.keys(item?.Translation || {}).length > 0
          ? item.Translation?.[l]?.Name || ''
          : item?.name || '',
      description:
        Object.keys(item?.Translation || {}).length > 0
          ? item.Translation?.[l]?.Description || ''
          : item?.description || '',
    };
  });

  return translatedItems.map((item) => {
    const subFind = translatedSubs.find((s) => s._id === item.categoryid);
    if (!subFind) return item;
    const attributes =
      item?.attributes?.map((attr) => {
        const attrFind = translatedSubs.find((s) => s._id === attr._id);
        const options =
          attr?.options?.map((opt) => {
            const optFind = translatedSubs.find((s) => s._id === (opt?.maincategoryid || null));
            const optFind2 = translatedSubs.find((s) => s._id === (opt?.CategoryId || null));
            const optFind3 = translatedItems.find((mi) => mi._id === (opt?._id || null));
            let result = { ...opt };
            if (optFind) result = { ...result, maincategory: optFind.Category || '' };
            if (optFind2) result = { ...result, Category: optFind2.Category || '' };
            if (optFind3)
              result = { ...result, name: optFind3.name || '', Name: optFind3.name || '' };
            if (result.minlimit) return { ...result, minlimit: Number(result.minlimit) || null };
            return result;
          }) || [];
        if (attrFind) return { ...attr, name: attrFind.Category || '', options };
        return { ...attr, options };
      }) || [];
    return {
      ...item,
      category: subFind.Category || '',
      categorydesc: subFind.Description || '',
      attributes: item?.attributes?.length > 0 ? attributes : [],
    };
  });
}

// ─── Main Component ────────────────────────────────────────────────────────────

export default function RestaurantTogether() {
  const pathname = window.location.pathname;
  const restaurantId = pathname.split('/')?.[2];
  const tableNumber = decodeURIComponent(pathname.split('/')?.[3] || '');
  const navigate = useNavigate();

  const { userProfile, language, setLanguageFromRestaurant, clearRestaurantLanguageOverride } =
    useDataContext();
  const orderContext = useOrderContext();
  const { setRestaurant: setContextRestaurant } = useRestaurantContext();

  const [showGogiLanding, setShowGogiLanding] = useState(true);

  // ── Core state ──────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [restaurant, setRestaurant] = useState(null);
  const [categories, setCategories] = useState([]);
  const [categoriesCopy, setCategoriesCopy] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);

  // ── Session state ────────────────────────────────────────────────────────────
  const [session, setSession] = useState(null);
  // Screens: 'loading' | 'not-supported' | 'select-package' | 'join-confirm' | 'active' | 'ended'
  const [screen, setScreen] = useState('loading');
  const [showMechanics, setShowMechanics] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [packageQuantity, setPackageQuantity] = useState(0);
  const [addOnQuantities, setAddOnQuantities] = useState({});
  const [packageTab, setPackageTab] = useState('information');
  const [isCaptain, setIsCaptain] = useState(false);
  const [isRejoin, setIsRejoin] = useState(false);
  const [timerDisplay, setTimerDisplay] = useState('');
  const [memberCount, setMemberCount] = useState(0);
  const [isStartingSession, setIsStartingSession] = useState(false);
  const [isEndingSession, setIsEndingSession] = useState(false);
  const [showEndConfirm, setShowEndConfirm] = useState(false);
  const [endCountdown, setEndCountdown] = useState(10);
  const [isLeavingSession, setIsLeavingSession] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareLinkCopied, setShareLinkCopied] = useState(false);
  const [showReminder, setShowReminder] = useState(false);

  // ── Active session state ─────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('menu');

  const [sessionOrders, setSessionOrders] = useState([]);
  const [isSubmittingOrder, setIsSubmittingOrder] = useState(false);
  const [showParticipantsModal, setShowParticipantsModal] = useState(false);

  // ── Cart state ───────────────────────────────────────────────────────────────
  const [orderItems, setOrderItems] = useState([]);

  // ── Item selection state ─────────────────────────────────────────────────────
  const [isItemSelectionModalOpen, setIsItemSelectionModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemQuantity, setItemQuantity] = useState(1);
  const [selectedExtras, setSelectedExtras] = useState([]);
  const [selectedAttributes, setSelectedAttributes] = useState([]);
  const [itemComments, setItemComments] = useState('');
  const [editingCartId, setEditingCartId] = useState(null);
  const [isValidationErrorModalOpen, setIsValidationErrorModalOpen] = useState(false);
  const [validationErrorMessage, setValidationErrorMessage] = useState('');

  // ── Refs ─────────────────────────────────────────────────────────────────────
  const subNavRef = useRef(null);

  const t = translations[language] || translations['en'] || {};

  // ── Initial data fetch ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!restaurantId || !tableNumber) return;
    fetchAll();
    return () => clearRestaurantLanguageOverride();
  }, [restaurantId, tableNumber, userProfile]);

  const fetchAll = async () => {
    try {
      setLoading(true);

      const [restaurantResp, categoriesResp, subCategoriesResp, menuResp] = await Promise.all([
        axios.post(
          `${configData.SERVER_URL}/mobile/restaurant/fetchspecific?id=${restaurantId}`,
          { id: restaurantId },
          { headers: { Authorization: `Bearer ${userProfile?.token}` } },
        ),
        axios.get(`${configData.SERVER_URL}/highercategory/fetch?restaurant=${restaurantId}`),
        axios.get(`${configData.SERVER_URL}/menu/category/fetch?restaurant=${restaurantId}`),
        axios.get(`${configData.SERVER_URL}/menu/fetch?restaurant=${restaurantId}&category=All`),
      ]);

      const raw = {
        ...restaurantResp.data,
        availability: restaurantResp.data.AvailabilityOriginal,
      };
      setLanguageFromRestaurant(raw);

      let lang = language || 'en';
      if (raw?.currency?.code) lang = renderCurrencyLanguage(raw.currency.code);
      if (window.localStorage.getItem('user_lang')) lang = window.localStorage.getItem('user_lang');

      const restaurantLang = renderCurrencyLanguage(raw?.currency?.code);

      const processedSubs = sortBy(
        getSubCategoriesTranslation(subCategoriesResp.data, lang, restaurantLang),
        ['Order'],
      );
      const processedMenu = sortBy(
        getMenuTranslation(menuResp.data.data, subCategoriesResp.data, lang, restaurantLang),
        ['category'],
      );
      const rawCategories = categoriesResp.data.map((c) => ({
        ...c,
        availability: c.Availability,
      }));
      const processedCategories = getCategoriesTranslation(rawCategories, lang, restaurantLang);
      const filteredCategories = processedCategories.filter((c) => isCategoryProperlyAvailable(c));

      setRestaurant(raw);
      setContextRestaurant(raw);
      setCategories(filteredCategories);
      setCategoriesCopy(processedCategories);
      setSubCategories(processedSubs);
      setMenuItems(processedMenu);

      // Pre-validate the QR link before doing any heavy loading.
      try {
        const vResp = await axios.get(
          `${configData.SERVER_URL}/tablesession/validateqr?restaurant=${restaurantId}&tableNumber=${encodeURIComponent(tableNumber)}`,
        );
        if (!vResp.data?.valid) {
          setScreen('not-supported');
          setLoading(false);
          return;
        }
      } catch (_) {
        setScreen('not-supported');
        setLoading(false);
        return;
      }

      // Now check if feature is supported
      if (!raw.IsTableSession) {
        setScreen('not-supported');
        setLoading(false);
        return;
      }

      // Check for an existing active session (read-only, no side effects)
      try {
        const sessionResp = await axios.get(
          `${configData.SERVER_URL}/tablesession/getbytable?restaurant=${restaurantId}&tableNumber=${encodeURIComponent(tableNumber)}`,
        );
        const existingSession = sessionResp.data;

        if (!existingSession) {
          setScreen('select-package');
          setIsCaptain(true);
        } else {
          // An active session already exists at this table — skip the
          // "open in app or browser" landing so returning guests (and other
          // diners scanning the QR) go straight into the session.
          setShowGogiLanding(false);
          setSession(existingSession);
          setMemberCount(existingSession.Participants?.length || 1);
          const userId =
            userProfile?.profile?.userID?.toString() ||
            userProfile?.profile?._id?.toString() ||
            userProfile?._id?.toString() ||
            orderContext.guestId ||
            localStorage.getItem('guest-uui') ||
            '';
          const createdBy =
            (existingSession.CreatedBy?._id ?? existingSession.CreatedBy)?.toString() || '';
          const captain = !!userId && createdBy === userId;
          setIsCaptain(captain);

          const isInParticipants = existingSession.Participants?.some(
            (p) => (p?._id ?? p)?.toString() === userId,
          );
          // Captain who previously left: ask them to re-join via the same join-confirm screen
          const needsRejoin = captain && !isInParticipants;
          setIsRejoin(needsRejoin);

          if (captain && isInParticipants) {
            setScreen(existingSession.HigherCategory ? 'active' : 'select-package');
          } else if (!captain && isInParticipants && existingSession.HigherCategory) {
            // Returning participant (e.g. closed/reopened the browser, or rescanned the QR).
            // They've already joined this session, so skip the join-confirm screen.
            setScreen('active');
          } else if (needsRejoin && !existingSession.HigherCategory) {
            // No package picked yet — let them go pick one (handleStartSession will rejoin them)
            setScreen('select-package');
          } else {
            setScreen('join-confirm');
          }
        }
      } catch (_) {
        setScreen('select-package');
      }
    } catch (err) {
      console.error('RestaurantTogether fetch error:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const getUserId = () =>
    userProfile?.profile?.userID?.toString() ||
    userProfile?.profile?._id?.toString() ||
    userProfile?._id?.toString() ||
    '';

  // ── Timer (shows absolute end time, e.g. "9:00 PM") ─────────────────────────
  useEffect(() => {
    if (!session?.ExpiresAt) return;
    const endTime = new Date(session.ExpiresAt).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
    setTimerDisplay(endTime);
    const checkExpiry = () => {
      if (new Date(session.ExpiresAt) - new Date() <= 0) {
        setScreen('ended');
      }
    };
    checkExpiry();
    const id = setInterval(checkExpiry, 5000);
    return () => clearInterval(id);
  }, [session?.ExpiresAt]);

  // ── 25-minute reminder (fires once per session) ─────────────────────────────
  useEffect(() => {
    if (screen !== 'active' || !session?.ExpiresAt || !session?._id) return;
    const storageKey = `dine-together-reminded-${session._id}`;
    if (localStorage.getItem(storageKey)) return;
    const REMINDER_MS = 25 * 60 * 1000;
    const check = () => {
      const remaining = new Date(session.ExpiresAt) - new Date();
      if (remaining > 0 && remaining <= REMINDER_MS) {
        localStorage.setItem(storageKey, '1');
        setShowReminder(true);
        return true;
      }
      return false;
    };
    if (check()) return;
    const id = setInterval(() => {
      if (check()) clearInterval(id);
    }, 5000);
    return () => clearInterval(id);
  }, [screen, session?.ExpiresAt, session?._id]);

  // ── End session 10s countdown ────────────────────────────────────────────────
  useEffect(() => {
    if (!showEndConfirm) {
      setEndCountdown(10);
      return;
    }
    setEndCountdown(10);
    const id = setInterval(() => {
      setEndCountdown((n) => (n > 0 ? n - 1 : 0));
    }, 1000);
    return () => clearInterval(id);
  }, [showEndConfirm]);

  // ── Select-package reconciliation poll ───────────────────────────────────────
  // If multiple users scan the QR before anyone has started the session, they all
  // initially land on 'select-package' as captain. As soon as one of them creates
  // the session, the others see it here and transition out: captain stays (or
  // moves to 'active' if the package got locked); everyone else goes to 'join-confirm'.
  useEffect(() => {
    if (screen !== 'select-package') return;
    const poll = async () => {
      try {
        const resp = await axios.get(
          `${configData.SERVER_URL}/tablesession/getbytable?restaurant=${restaurantId}&tableNumber=${encodeURIComponent(tableNumber)}`,
        );
        const existing = resp.data;
        if (!existing) return;
        const userId =
          userProfile?.profile?.userID?.toString() ||
          userProfile?.profile?._id?.toString() ||
          userProfile?._id?.toString() ||
          orderContext.guestId ||
          localStorage.getItem('guest-uui') ||
          '';
        const createdBy = (existing.CreatedBy?._id ?? existing.CreatedBy)?.toString() || '';
        const captain = !!userId && createdBy === userId;
        const isInParticipants = existing.Participants?.some(
          (p) => (p?._id ?? p)?.toString() === userId,
        );
        setSession(existing);
        setMemberCount(existing.Participants?.length || 1);
        setIsCaptain(captain);
        if (captain) {
          if (existing.HigherCategory) setScreen('active');
        } else if (isInParticipants && existing.HigherCategory) {
          // Returning participant — skip the join-confirm screen and go straight to active.
          setScreen('active');
        } else {
          setScreen('join-confirm');
        }
      } catch (_) {
        // silently fail polling
      }
    };
    const id = setInterval(poll, 5000);
    return () => clearInterval(id);
  }, [screen, restaurantId, tableNumber, userProfile, orderContext.guestId]);

  // ── Session polling (detect remote close) ───────────────────────────────────
  useEffect(() => {
    if (screen !== 'active') return;
    const poll = async () => {
      try {
        const resp = await axios.get(
          `${configData.SERVER_URL}/tablesession/get?restaurant=${restaurantId}&tableNumber=${encodeURIComponent(tableNumber)}`,
        );
        if (!resp.data || resp.data.Status === 'closed') {
          setScreen('ended');
        } else {
          setSession(resp.data);
          setMemberCount(resp.data.Participants?.length || memberCount);
        }
      } catch (_) {
        // silently fail polling
      }
    };
    const id = setInterval(poll, 5000);
    return () => clearInterval(id);
  }, [screen, restaurantId, tableNumber]);

  // ── Session orders feed ───────────────────────────────────────────────────────
  const fetchSessionOrders = useCallback(async () => {
    if (!session?._id) return;
    try {
      const resp = await axios.post(
        `${configData.SERVER_URL}/tablesession/orders`,
        { sessionId: session._id },
        { headers: { Authorization: `Bearer ${userProfile?.token}` } },
      );
      setSessionOrders(resp.data || []);
    } catch (_) {}
  }, [session?._id, userProfile?.token]);

  useEffect(() => {
    if (screen !== 'active') return;
    fetchSessionOrders();
    const id = setInterval(fetchSessionOrders, 5000);
    return () => clearInterval(id);
  }, [screen, fetchSessionOrders]);

  // ── Grouped menu items ────────────────────────────────────────────────────────
  const hasHigherCategories = categories.length > 0;

  const groupedMenuItems = useMemo(() => {
    if (!menuItems?.length || !hasHigherCategories) {
      return !menuItems?.length
        ? []
        : chain(menuItems)
            .groupBy('category')
            .map((items, category) => ({
              category,
              higherCategoryId: null,
              menu: items.sort((a, b) => (a.order || 0) - (b.order || 0)),
            }))
            .value();
    }
    const subToParents = {};
    categories.forEach((cat) => {
      cat.TaggedCategories?.forEach((tagged) => {
        if (!subToParents[tagged._id]) subToParents[tagged._id] = [];
        subToParents[tagged._id].push(cat._id);
      });
    });
    const expanded = [];
    menuItems.forEach((item) => {
      const parents = subToParents[item.categoryid] || [null];
      parents.forEach((pid) => expanded.push({ ...item, higherCategoryId: pid }));
    });
    return chain(expanded)
      .groupBy((item) => `${item.higherCategoryId}_${item.category}`)
      .map((items) => ({
        category: items[0]?.category,
        higherCategoryId: items[0]?.higherCategoryId || null,
        menu: items.sort((a, b) => (a.order || 0) - (b.order || 0)),
      }))
      .sortBy((group) => {
        const parent = categories.find((c) => c._id === group.higherCategoryId);
        return parent?.Order ?? 999;
      })
      .value()
      .filter((item) => item.higherCategoryId);
  }, [menuItems, categories, hasHigherCategories]);

  const filteredGroupedMenuItems = useMemo(() => {
    return groupedMenuItems
      .filter((group) => {
        const sub = subCategories.find((s) => s.Category === group.category);
        if (!sub) return true;
        if (sub.IsHidden) return false;
        if (sub.HideOnDineIn) return false;
        return true;
      })
      .map((group) => ({
        ...group,
        menu: group.menu.filter((item) => !item.hideondinein),
      }))
      .filter((group) => group.menu.length > 0)
      .sort((a, b) => {
        const ia = categories.findIndex((c) => c._id === a.higherCategoryId);
        const ib = categories.findIndex((c) => c._id === b.higherCategoryId);
        if (ia !== ib) return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
        const sa = subCategories.find((s) => s.Category === a.category);
        const sb = subCategories.find((s) => s.Category === b.category);
        return (sa?.Order ?? 999) - (sb?.Order ?? 999);
      });
  }, [groupedMenuItems, subCategories, categories]);

  // Filter to only the locked higher category for the active session
  const sessionMenuItems = useMemo(
    () =>
      session?.HigherCategory?._id
        ? filteredGroupedMenuItems.filter((g) => g.higherCategoryId === session.HigherCategory._id)
        : filteredGroupedMenuItems,
    [filteredGroupedMenuItems, session?.HigherCategory?._id],
  );

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const scrollSpyData = useMemo(() => {
    const relevantCategories = session?.HigherCategory?._id
      ? categories.filter((c) => c._id === session.HigherCategory._id)
      : categories;
    return relevantCategories.map((cat) => ({
      ...cat,
      sections: sessionMenuItems
        .filter((g) => g.higherCategoryId === cat._id)
        .filter((g) =>
          subCategories.some((s) => !s.IsHidden && !s.HideOnDineIn && s.Category === g.category),
        ),
    }));
  }, [categories, sessionMenuItems, subCategories, session?.HigherCategory?._id]);

  const {
    activeCategory,
    activeSubCategory,
    scrollTo,
    subNavRef: scrollSubNavRef,
    setActiveCategory,
    setActiveSubCategory,
    // eslint-disable-next-line react-hooks/rules-of-hooks
  } = useScrollSpy(scrollSpyData, {
    offset: 180,
    getCategoryId: (cat) => cat._id,
    getSections: (cat) => cat.sections,
    getSectionId: (section) => `${section.higherCategoryId}_${section.category}`,
  });

  const selectedCategory = categoriesCopy.find((c) => c._id === activeCategory) || null;

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const filteredSubCategories = useMemo(() => {
    const visible = subCategories.filter((s) => !s.IsHidden && !s.HideOnDineIn);
    if (!session?.HigherCategory?._id) {
      return visible.filter((s) => sessionMenuItems.some((g) => g.category === s.Category));
    }
    const lockedCat = categoriesCopy.find((c) => c._id === session.HigherCategory._id);
    if (!lockedCat) return [];
    return visible.filter(
      (s) =>
        lockedCat.TaggedCategories?.some((t) => t._id === s._id) &&
        sessionMenuItems.some((g) => g.category === s.Category),
    );
  }, [subCategories, sessionMenuItems, session?.HigherCategory?._id, categoriesCopy]);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const currentSubCategories = useMemo(() => {
    const cur = scrollSpyData.find((c) => c._id === activeCategory);
    return cur?.sections || [];
  }, [scrollSpyData, activeCategory]);

  // ── Package (higher category) selection ──────────────────────────────────────
  const handlePackageQuantityChange = (cat, next) => {
    if (next === 0) {
      setSelectedPackage(null);
      setPackageQuantity(0);
      setAddOnQuantities({});
    } else {
      if (selectedPackage?._id !== cat._id) setAddOnQuantities({});
      setSelectedPackage(cat);
      setPackageQuantity(next);
    }
  };

  const handleConfirmPackage = () => {
    if (!selectedPackage) return;
    setShowMechanics(true);
  };

  // Returns a real MongoDB ObjectId string to use as guestId for unauthenticated users.
  // Re-uses the stored id if it looks like a valid ObjectId; otherwise creates a new guest account.
  const resolveGuestId = async () => {
    const stored = orderContext.guestId || localStorage.getItem('guest-uui');
    if (stored && /^[a-f\d]{24}$/i.test(stored)) return stored;
    const resp = await axios.post(`${configData.SERVER_URL}/orders/guest/createuser`, {
      fromWebStandAlone: true,
    });
    const id = resp.data._id.toString();
    orderContext.setGuestId(id);
    orderContext.setIsGuest(true);
    localStorage.setItem('guest-uui', id);
    return id;
  };

  // Builds the auth config and optional guestId for tablesession requests.
  const buildSessionRequestArgs = async () => {
    if (userProfile?.token) {
      return {
        authConfig: { headers: { Authorization: `Bearer ${userProfile.token}` } },
        guestId: null,
      };
    }
    const guestId = await resolveGuestId();
    return { authConfig: undefined, guestId };
  };

  const handleStartSession = async () => {
    if (!selectedPackage) return;
    setIsStartingSession(true);
    try {
      const { authConfig, guestId } = await buildSessionRequestArgs();
      // Mirror the display: the allowlist is the package's populated AddOnCategories
      // (matches the backend filter in /tablesession/join). Keep this in sync with the
      // add-ons rendered in the package list so a selected add-on is never silently dropped.
      const allowedAddOnIds = new Set(
        (selectedPackage.AddOnCategories || []).map((a) => (a._id || a).toString()),
      );
      const addOnPackages = Object.entries(addOnQuantities)
        .filter(([id, qty]) => qty > 0 && allowedAddOnIds.has(id))
        .map(([higherCategoryId, quantity]) => ({ higherCategoryId, quantity }));
      const body = {
        restaurant: restaurantId,
        tableNumber,
        higherCategoryId: selectedPackage._id,
        ...(guestId && { guestId }),
        ...(packageQuantity > 0 && { quantity: packageQuantity }),
        ...(addOnPackages.length > 0 && { addOnPackages }),
      };
      const resp = await axios.post(`${configData.SERVER_URL}/tablesession/join`, body, authConfig);
      const sess = resp.data;
      setSession(sess);
      setMemberCount(sess.Participants?.length || 1);
      const userId = getUserId() || guestId || '';
      const createdBy = (sess.CreatedBy?._id ?? sess.CreatedBy)?.toString() || '';
      const captain = !!userId && createdBy === userId;
      setIsCaptain(captain);
      // Race fallback: a different scanner created the session in the same instant. The
      // backend's unique partial index landed us as a participant in their session instead.
      if (!captain) {
        toast.info(
          t.dine_together_already_started ||
            "Another guest started the session first. You've joined their session.",
        );
      }
      setShowMechanics(false);
      setScreen('active');
    } catch (err) {
      toast.error(
        err?.response?.data?.message || t.dine_together_start_error || 'Failed to start session.',
      );
    } finally {
      setIsStartingSession(false);
    }
  };

  const handleJoinConfirm = () => {
    setShowMechanics(true);
  };

  const handleJoinComplete = async () => {
    setShowMechanics(false);
    try {
      const { authConfig, guestId } = await buildSessionRequestArgs();
      const body = {
        restaurant: restaurantId,
        tableNumber,
        ...(guestId && { guestId }),
      };
      const resp = await axios.post(`${configData.SERVER_URL}/tablesession/join`, body, authConfig);
      setSession(resp.data);
      setMemberCount(resp.data.Participants?.length || memberCount);
      setScreen('active');
    } catch (err) {
      toast.error(
        err?.response?.data?.message || t.dine_together_join_error || 'Failed to join session.',
      );
    }
  };

  // ── Leave session ────────────────────────────────────────────────────
  const handleLeaveSession = async () => {
    setIsLeavingSession(true);
    try {
      const { authConfig, guestId } = await buildSessionRequestArgs();
      await axios.delete(`${configData.SERVER_URL}/tablesession/leave`, {
        data: { sessionId: session._id, ...(guestId && { guestId }) },
        ...authConfig,
      });
    } catch (_) {}
    setShowLeaveConfirm(false);
    setIsLeavingSession(false);
    navigate('/');
  };

  // ── End session (Captain only) ────────────────────────────────────────────────
  const handleEndSession = async () => {
    setIsEndingSession(true);
    try {
      const { authConfig, guestId } = await buildSessionRequestArgs();
      await axios.put(
        `${configData.SERVER_URL}/tablesession/closeparticipant`,
        { sessionId: session._id, ...(guestId && { guestId }) },
        authConfig,
      );
    } catch (_) {}
    setShowEndConfirm(false);
    setScreen('ended');
    setIsEndingSession(false);
  };

  // ── Item selection ────────────────────────────────────────────────────────────
  const handleUpdateAttributesFromDefault = (item) => {
    if (!item?.attributes?.length) return [];
    const initial = [];
    item.attributes.forEach((attr) => {
      if (attr.belongsto || !attr.isMultiple || !attr.options?.length) return;
      const opts = attr.options
        .filter((opt) => {
          const dv = opt.defaultValue;
          const v = dv !== null && dv !== undefined && dv !== '' ? parseInt(dv, 10) : 0;
          return !isNaN(v) && v > 0;
        })
        .map((opt) => ({
          id: opt._id,
          name: opt?.name || opt?.Name || 'Unknown',
          additionalPrice: opt?.additionalPrice || 0,
          price: opt?.price || opt?.Price || 0,
          quantity: parseInt(opt.defaultValue, 10),
          isMultiple: attr?.isMultiple || false,
          isconnectedto: opt?.isconnectedto || null,
        }));
      if (opts.length > 0)
        initial.push({ id: attr._id, attributeId: attr._id, options: opts, isMultiple: true });
    });
    return initial;
  };

  const handleItemSelectionModalOpen = (item) => {
    setSelectedItem({ ...item, higherCategoryId: selectedCategory?._id || null });
    setItemQuantity(1);
    setSelectedExtras([]);
    setSelectedAttributes(handleUpdateAttributesFromDefault(item) || []);
    setItemComments('');
    setEditingCartId(null);
    setIsItemSelectionModalOpen(true);
  };

  const handleEditCartItem = (cartId) => {
    const existing = orderItems.find((i) => i.cartId === cartId);
    const menuItem = menuItems.find((mi) => mi._id === existing?._id);
    if (existing && menuItem) {
      setSelectedItem({ ...menuItem, higherCategoryId: selectedCategory?._id || null });
      setItemQuantity(existing.quantity);
      setSelectedExtras(existing.extras || []);
      setSelectedAttributes(existing.selectedAttributes || []);
      setItemComments(existing.comments || '');
      setEditingCartId(cartId);
      setIsItemSelectionModalOpen(true);
    }
  };

  const validateRequiredAttributes = () => {
    if (!selectedItem) return true;
    const optsBelowMin = [];
    let totalQty = 0;
    (selectedItem.attributes || []).forEach((attr) => {
      if (attr?.belongsto) return;
      attr?.options?.forEach((srcOpt) => {
        const attrSel = selectedAttributes?.find(
          (a) => a.attributeId?.toString() === attr._id?.toString(),
        );
        const selOpt = attrSel?.options?.find((o) => o.id?.toString() === srcOpt._id?.toString());
        const qty = selOpt?.quantity || 0;
        totalQty += qty;
        if (srcOpt?.minlimit && qty < srcOpt.minlimit)
          optsBelowMin.push({ ...srcOpt, quantity: qty });
      });
    });

    const firstAttr = selectedItem.attributes?.[0];
    if (
      firstAttr?.minNumberAllowedTotalItem &&
      totalQty < Number(firstAttr.minNumberAllowedTotalItem)
    ) {
      setValidationErrorMessage(
        `${t.total || 'Total'} ${t.minimum || 'Minimum'}: ${firstAttr.minNumberAllowedTotalItem} | ${t.total || 'Total'} ${t.selected || 'Selected'}: ${totalQty || 0}`,
      );
      return false;
    }
    if (optsBelowMin.length > 0) {
      setValidationErrorMessage(
        `${t.restaurant_menu_minimum_quantity_message}:\n${optsBelowMin.map((o) => `${o.name} (Min: ${o.minlimit} | Selected: ${o.quantity || 0})`).join('\n')}`,
      );
      return false;
    }
    const required = selectedItem.attributes?.filter((a) => a.attributesRequired) || [];
    for (const req of required) {
      const sel = selectedAttributes
        ?.filter((a) =>
          req?.isMultiple ? a?.options.some((o) => o.quantity && o.quantity > 0) : a,
        )
        ?.find((a) => a.attributeId?.toString() === req._id?.toString());
      if (!req.belongsto && (!sel || !sel?.options || sel.options.length === 0)) {
        setValidationErrorMessage(`${t.restaurant_menu_select_option_message} "${req.name}"`);
        return false;
      }
    }
    return true;
  };

  const calculateItemTotal = (item, qty) => {
    let price = item?.price || 0;
    item?.extras?.forEach((e) => {
      price += Number(e?.price || 0) * Number(e?.quantity || 1);
    });
    item?.selectedAttributes?.forEach((attr) => {
      if (attr?.isMultiple) {
        attr?.options?.forEach((opt) => {
          if (opt && (opt?.Price || opt?.additionalPrice)) {
            if (attr?.connectedFromId) {
              const parent = item?.selectedAttributes
                ?.flatMap((a) => a?.options || [])
                .find((o) => o?.id === attr?.connectedFromId);
              price += Number(opt?.Price || 0) * Number(parent?.quantity || 1);
            } else {
              price += Number(opt?.additionalPrice || 0) * Number(opt?.quantity || 1);
            }
          }
        });
        return;
      }
      attr?.options?.forEach((opt) => {
        if (opt && (opt?.Price || opt?.additionalPrice)) {
          if (attr?.connectedFromId) {
            const parent = item?.selectedAttributes
              ?.flatMap((a) => a?.options || [])
              .find((o) => o?.id === attr?.connectedFromId);
            price += Number(opt?.Price || 0) * Number(parent?.quantity || 1);
          } else {
            price += Number(opt?.additionalPrice || 0);
          }
        }
      });
    });
    return price * qty;
  };

  const calculateModalTotalPrice = () => {
    let total = selectedItem?.price || 0;
    selectedExtras?.forEach((e) => {
      total += Number(e?.price || 0) * Number(e?.quantity || 1);
    });
    selectedAttributes?.forEach((attr) => {
      if (attr?.isMultiple) {
        attr?.options?.forEach((opt) => {
          if (opt && (opt?.Price || opt?.additionalPrice)) {
            if (attr?.connectedFromId) {
              const parent = selectedAttributes
                ?.flatMap((a) => a?.options || [])
                .find((o) => o?.id === attr?.connectedFromId);
              total += Number(opt?.Price || 0) * Number(parent?.quantity || 1);
            } else {
              total += Number(opt?.additionalPrice || 0) * Number(opt?.quantity || 1);
            }
          }
        });
        return;
      }
      attr?.options?.forEach((opt) => {
        if (opt && (opt?.Price || opt?.additionalPrice)) {
          if (attr?.connectedFromId) {
            const parent = selectedAttributes
              ?.flatMap((a) => a?.options || [])
              .find((o) => o?.id === attr?.connectedFromId);
            total += Number(opt?.Price || 0) * Number(parent?.quantity || 1);
          } else {
            total += Number(opt?.additionalPrice || 0);
          }
        }
      });
    });
    return total * itemQuantity;
  };

  const haveSameConfig = (i1, i2) => {
    if (i1.comments !== i2.comments) return false;
    const e1 = i1.extras || [],
      e2 = i2.extras || [];
    if (e1.length !== e2.length) return false;
    for (const x of e1) {
      if (!e2.find((y) => y.id === x.id && y.quantity === x.quantity)) return false;
    }
    const a1 = i1.selectedAttributes || [],
      a2 = i2.selectedAttributes || [];
    if (a1.length !== a2.length) return false;
    for (const attr1 of a1) {
      const attr2 = a2.find((a) => a.attributeId === attr1.attributeId);
      if (!attr2) return false;
      if ((attr1.options || []).length !== (attr2.options || []).length) return false;
      for (const o1 of attr1.options || []) {
        if (
          !attr2.options?.find((o2) => o2.id === o1.id && (o2.quantity || 1) === (o1.quantity || 1))
        )
          return false;
      }
    }
    return true;
  };

  const handleAddToOrder = () => {
    if (!validateRequiredAttributes()) {
      setIsValidationErrorModalOpen(true);
      return;
    }
    const attrs = selectedAttributes.map((sel) => {
      const attr = selectedItem?.attributes?.find(
        (a) => a._id?.toString() === sel?.attributeId?.toString(),
      );
      return {
        id: sel?.attributeId,
        attributeId: sel?.attributeId,
        connectedFromId: sel?.connectedFromId || null,
        attributeName: attr?.name || t.unknown || 'Unknown',
        options: sel?.options,
        isMultiple: attr?.isMultiple || false,
      };
    });
    const newItem = {
      ...selectedItem,
      cartId: editingCartId || uniqueId('cart_'),
      quantity: itemQuantity,
      extras: selectedExtras,
      selectedAttributes: attrs,
      comments: itemComments,
      totalPrice: calculateItemTotal(
        { ...selectedItem, extras: selectedExtras, selectedAttributes: attrs },
        itemQuantity,
      ),
    };
    if (editingCartId) {
      setOrderItems((prev) => prev.map((i) => (i.cartId === editingCartId ? newItem : i)));
    } else {
      setOrderItems((prev) => {
        const dup = prev.find((i) => i._id === newItem._id && haveSameConfig(i, newItem));
        if (dup)
          return prev.map((i) =>
            i.cartId === dup.cartId
              ? {
                  ...i,
                  quantity: i.quantity + itemQuantity,
                  totalPrice: calculateItemTotal(i, i.quantity + itemQuantity),
                }
              : i,
          );
        return [...prev, newItem];
      });
    }
    setIsItemSelectionModalOpen(false);
  };

  const handleRemoveFromOrder = (cartId) => {
    setOrderItems((prev) => prev.filter((i) => i.cartId !== cartId));
  };

  const handleUpdateItemQuantity = (cartId, qty) => {
    if (qty <= 0) {
      handleRemoveFromOrder(cartId);
      return;
    }
    setOrderItems((prev) =>
      prev.map((i) =>
        i.cartId === cartId ? { ...i, quantity: qty, totalPrice: calculateItemTotal(i, qty) } : i,
      ),
    );
  };

  // Attribute change handlers
  const handleRadioAttributeChange = (attributeId, selectedOption, connectedFromId = null) => {
    setSelectedAttributes((prev) => {
      const filtered = prev.filter((a) => {
        if (a.attributeId === attributeId) return false;
        if (connectedFromId) {
          const prevOpt = prev.find((x) => x.attributeId === attributeId)?.options?.[0];
          if (prevOpt?.isconnectedto) {
            return !prevOpt.isconnectedto.some(
              (id) => id?.toString() === a.attributeId?.toString(),
            );
          }
        }
        if (a.connectedFromId) {
          const prevOpt = prev.find((x) => x.attributeId === attributeId)?.options?.[0];
          const prevConnected = prevOpt?.id?.toString();
          if (prevConnected && a.connectedFromId?.toString() === prevConnected) return false;
        }
        return true;
      });
      if (!selectedOption) return filtered;
      return [
        ...filtered,
        { id: attributeId, attributeId, connectedFromId, options: [selectedOption] },
      ];
    });
  };

  const handleMultipleAttributeChange = (attributeId, isSelected, option) => {
    setSelectedAttributes((prev) => {
      const idx = prev.findIndex((a) => a.attributeId === attributeId);
      if (idx === -1 && isSelected)
        return [...prev, { id: attributeId, attributeId, options: [option] }];
      if (idx !== -1) {
        const updated = [...prev];
        const attr = selectedItem?.attributes?.find(
          (a) => a._id?.toString() === attributeId?.toString(),
        );
        if (isSelected) {
          updated[idx] = {
            ...updated[idx],
            options: attr?.isMultiple ? [...updated[idx].options, option] : [option],
          };
        } else {
          updated[idx] = {
            ...updated[idx],
            options: updated[idx].options.filter((o) => o.id !== option.id),
          };
          if (updated[idx].options.length === 0) updated.splice(idx, 1);
        }
        return updated;
      }
      return prev;
    });
  };

  const handleMultipleCounterChange = (attributeId, itemId, newCounts) => {
    setSelectedAttributes((prev) => {
      const attr = selectedItem?.attributes?.find(
        (a) => a._id?.toString() === attributeId?.toString(),
      );
      const opts = Object.entries(newCounts)
        .filter(([, c]) => c > 0)
        .map(([oid, count]) => {
          const opt = attr?.options?.find((o) => o._id?.toString() === oid?.toString());
          return opt
            ? {
                id: opt._id,
                name: opt?.name || 'Unknown',
                additionalPrice: opt?.additionalPrice || 0,
                price: opt?.price || 0,
                quantity: count,
                isMultiple: attr?.isMultiple || false,
                isconnectedto: opt?.isconnectedto || null,
              }
            : null;
        })
        .filter(Boolean);

      const idx = prev.findIndex((a) => a.attributeId === attributeId);
      const curQty = newCounts[itemId];
      if (curQty === 0) {
        const curIdStr = itemId?.toString();
        const filtered = prev.filter((a) => {
          if (a.connectedFromId?.toString() === curIdStr) return false;
          if (a.options?.some((o) => o?.isconnectedto?.toString() === curIdStr)) return false;
          return true;
        });
        if (opts.length > 0 && idx !== -1) {
          filtered[idx] = { ...prev[idx], options: opts };
          return filtered;
        }
        return filtered.filter((a) => a.attributeId?.toString() !== attributeId?.toString());
      }
      if (idx === -1) {
        if (opts.length > 0)
          return [
            ...prev,
            { id: attributeId, attributeId, options: opts, isMultiple: attr?.isMultiple || false },
          ];
        return prev;
      }
      const updated = [...prev];
      updated[idx] = { ...updated[idx], options: opts };
      return updated;
    });
  };

  const handleExtrasChange = (newCounts) => {
    setSelectedExtras(
      Object.entries(newCounts)
        .filter(([, c]) => c > 0)
        .map(([id, count]) => {
          const extra = selectedItem?.extras?.find((e) => e._id === id);
          return { id, name: extra?.name || 'Unknown', price: extra?.price || 0, quantity: count };
        }),
    );
  };

  // ── Format orders for API ─────────────────────────────────────────────────────
  const formatOrdersForCheckout = (items) =>
    items.map((item) => ({
      _id: item._id,
      qty: item.quantity,
      extras: (item.extras || [])
        .filter((e) => e.quantity > 0)
        .map((e) => ({ _id: e.id, qty: e.quantity })),
      attributes: (item.selectedAttributes || []).flatMap((attr) =>
        (attr.options || []).map((opt) => ({
          _id: attr.attributeId,
          value: opt.id,
          qty: opt.quantity || 1,
          isMultiple: attr?.isMultiple || false,
        })),
      ),
      notes: item.comments || '',
    }));

  // ── Place order ───────────────────────────────────────────────────────────────
  const handlePlaceOrder = async () => {
    const isGuest =
      !userProfile.token &&
      orderContext.isGuest &&
      localStorage.getItem('guest-uui') !== undefined &&
      localStorage.getItem('guest-uui') !== null;

    if (orderItems.length === 0 || !session?._id) return;
    setIsSubmittingOrder(true);
    try {
      const payload = {
        restaurantId: restaurant._id,
        date: new Date(),
        isDineIn: true,
        tableNumber,
        order: formatOrdersForCheckout(orderItems),
        isweb: true,
        sessionId: session._id,
        TableSession: session._id,
        payment_method: 'tablesession',
        mobilenumber: userProfile?.profile?.mobilenumber || '',
        notes: '',
        isGuest: isGuest,
        guestId: isGuest ? orderContext.guestId : '',
      };
      await axios.post(`${configData.SERVER_URL}/orders/v2/preorder`, payload, {
        headers: { Authorization: `Bearer ${userProfile?.token}` },
      });
      setOrderItems([]);
      toast.success(t.dine_together_order_submitted || 'Order sent to kitchen!');
      setActiveTab('orders');
      fetchSessionOrders();
    } catch (err) {
      console.error('Order error:', err);
      // Was reading lowercase `message` only, so every capital-M envelope the
      // backend sends — closed, menu hours, sold out — fell through to the
      // generic string.
      toast.error(
        guestFacingError(err, t.dine_together_order_failed || 'Failed to submit order. Try again.'),
      );
    } finally {
      setIsSubmittingOrder(false);
    }
  };

  // ── Cart total ────────────────────────────────────────────────────────────────
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const cartTotal = useMemo(
    () =>
      orderItems.reduce(
        (sum, item) => sum + (item.totalPrice || calculateItemTotal(item, item.quantity)),
        0,
      ),
    [orderItems],
  );

  // ── Item selection modal pieces ───────────────────────────────────────────────
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const extrasItems = useMemo(
    () =>
      (selectedItem?.extras || []).map((e) => ({
        id: e._id,
        label: e.name,
        price: e.price,
        initialCount: 0,
      })),
    [selectedItem],
  );

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const groupedAttributes = useMemo(
    () =>
      (selectedItem?.attributes || []).reduce((acc, attr) => {
        if (attr.belongsto) return acc;
        const opts = attr.options?.map((opt) => ({
          ...opt,
          id: opt._id,
          name: opt.name,
          additionalPrice: opt.additionalPrice,
          price: opt.additionalPrice || 0,
          priceLabel: opt.additionalPrice
            ? `${restaurant?.currency?.code || 'DKK'} ${opt.additionalPrice}`
            : '',
          isconnectedto: opt?.isconnectedto || null,
        }));
        if (opts?.length > 0) acc.push({ ...attr, id: attr._id, options: opts });
        return acc;
      }, []),
    [selectedItem, restaurant?.currency?.code],
  );

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const ItemSelectionFooter = useCallback(
    () => (
      <div className="bg-white !p-3 sm:!p-6 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between rounded-b-2xl gap-3 sm:gap-0">
        <div className="w-full max-w-[250px] flex items-center justify-between">
          <Counter
            className="p-2 border border-gray-200 rounded-[50px]"
            initialValue={itemQuantity}
            value={itemQuantity}
            onChange={setItemQuantity}
          />
          <p className="font-bold text-sm sm:text-base">
            {restaurant?.currency?.code || 'DKK'} {calculateModalTotalPrice().toFixed(2)}
          </p>
        </div>
        <button
          className={`w-full sm:w-auto bg-primary text-xs sm:text-sm text-white font-bold px-4 py-[10px] sm:py-[14px] rounded-full hover:bg-secondary transition-colors ${itemQuantity <= 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
          onClick={handleAddToOrder}
          disabled={itemQuantity <= 0}
        >
          <i className="fa fa-plus mr-2" style={{ fontSize: 12 }} />
          {editingCartId ? t.restaurant_menu_order_item_edit : t.restaurant_menu_order_item_add}
        </button>
      </div>
    ),
    [
      itemQuantity,
      editingCartId,
      calculateModalTotalPrice,
      handleAddToOrder,
      restaurant?.currency?.code,
      t,
    ],
  );

  // ── Venue landing (Gogi, Maido) ───────────────────────────────────────────────
  // "Order via app or browser" before the guest joins the table's group order.
  const Landing =
    restaurantId === MAIDO_RESTAURANT_ID
      ? MaidoLanding
      : restaurantId === GOGI_RESTAURANT_ID ||
          restaurantId === '5facf0eb03ebf8001774c4c2' ||
          restaurantId === '6014fb1ba41c0400154691a8'
        ? GogiLanding
        : null;

  if (showGogiLanding && Landing) {
    if (loading) return <Loader />;

    return (
      <Landing
        onOrderViaBrowser={() => setShowGogiLanding(false)}
        onOrderViaMobile={() => {
          const deepLink = `heywaitr://restaurant-together/${restaurantId}/${encodeURIComponent(tableNumber)}`;
          window.location.href = deepLink;
        }}
      />
    );
  }

  // ── Render guards ─────────────────────────────────────────────────────────────
  if (loading) return <Loader />;
  if (error)
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] p-8 text-center">
        <p className="text-gray-500 mb-4">{t.restaurant_menu_no_data || 'Error loading data'}</p>
        <button
          className="bg-primary text-white px-6 py-3 rounded-full font-semibold"
          onClick={() => navigate('/')}
        >
          {t.back || 'Go back'}
        </button>
      </div>
    );
  if (!restaurant) return null;
  if (restaurant.orderingPaused) {
    return <OrderingPausedScreen message={restaurant.orderingPausedMessage} />;
  }

  const primaryColor = restaurant?.PrimaryColor || '#96c11f';
  const secondaryColor = restaurant?.SecondaryColor || '#6f9800';
  const textColor = restaurant?.TextColor || '#ffffff';
  const currency = restaurant?.currency?.code || 'DKK';

  // ── Not supported screen ──────────────────────────────────────────────────────
  if (screen === 'not-supported') {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] p-8 text-center max-w-sm mx-auto">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
          <i className="fa fa-times-circle text-gray-400 text-2xl" />
        </div>
        <h2 className="text-xl font-bold text-gray-800 mb-2">
          {t.dine_together_not_supported_title || 'Feature not available'}
        </h2>
        <p className="text-gray-500 text-sm mb-6">
          {t.dine_together_not_supported_desc ||
            'This restaurant does not support Group Dining sessions.'}
        </p>
        <button
          className="bg-primary text-white px-8 py-3 rounded-full font-semibold hover:bg-secondary transition-colors"
          style={{ '--primary': primaryColor, '--secondary': secondaryColor }}
          onClick={() => navigate('/')}
        >
          {t.back || 'Go back'}
        </button>
      </div>
    );
  }

  // ── Session ended screen ──────────────────────────────────────────────────────
  if (screen === 'ended') {
    return (
      <div
        className="together-wrap-screen"
        style={{ '--primary': primaryColor, '--secondary': secondaryColor, '--text': textColor }}
      >
        {/* Invoice icon */}
        <div className="mb-6 w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center">
          <i className="fa fa-receipt text-primary text-4xl" />
        </div>
        <h1 className="text-3xl font-bold text-gray-900 mb-3">
          {t.dine_together_wrap_title || "That's a wrap!"}
        </h1>
        <p className="text-gray-500 text-base max-w-xs mb-8 leading-relaxed">
          {t.dine_together_wrap_subtitle ||
            'We hope you enjoyed your meal! To pay, our staff will be happy to help, or feel free to visit the counter.'}
        </p>

        <div className="flex flex-col items-center gap-3">
          <button
            className="bg-primary hover:bg-secondary text-white font-bold px-10 py-4 rounded-full transition-colors text-sm md:text-base flex items-center gap-2"
            onClick={() => navigate('/')}
          >
            <i className="fa fa-heart" />
            {t.dine_together_thanks_cta || 'Thanks for having us!'}
          </button>
          <button
            className="text-primary hover:underline font-semibold text-sm flex items-center gap-2 py-2"
            onClick={() => {
              setSession(null);
              setSessionOrders([]);
              setSelectedPackage(null);
              setPackageQuantity(0);
              setAddOnQuantities({});
              setIsCaptain(true);
              setIsRejoin(false);
              setMemberCount(1);
              setScreen('select-package');
            }}
          >
            <i className="fa fa-redo" />
            {t.dine_together_start_new_session || 'Start a new session'}
          </button>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════════
  // Main render — session screens share common CSS vars wrapper
  // ═══════════════════════════════════════════════════════════════════════════════
  return (
    <div
      className="together-container"
      style={{
        '--primary': primaryColor,
        '--primary-rgb': hexToRgbString(primaryColor),
        '--secondary': secondaryColor,
        '--text': textColor,
      }}
    >
      {/* ── SELECT PACKAGE SCREEN (Captain) ───────────────────────────────────── */}
      {screen === 'select-package' && (
        <div className="together-page max-w-lg mx-auto pb-32">
          {/* Restaurant cover + info */}
          <div className="relative overflow-hidden" style={{ height: 260 }}>
            <img
              src={restaurant.cover}
              alt={restaurant.name}
              className="w-full h-full object-cover"
            />
            <div
              className="absolute inset-0 flex flex-col justify-end p-4 pb-5"
              style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 60%)' }}
            >
              <div className="flex items-center gap-2 mb-1">
                <span className="bg-white/20 backdrop-blur-sm text-white text-xs font-semibold px-3 py-1 rounded-full border border-white/30">
                  <i className="fa fa-table mr-1" />
                  {t.dine_together_table_label || 'Table'} {tableNumber}
                </span>
                <span className="bg-primary text-white text-xs font-semibold px-3 py-1 rounded-full">
                  {t.dine_together_status_open || 'Open'}
                </span>
              </div>
              <h1 className="text-white text-2xl font-bold">{restaurant.name}</h1>
            </div>
          </div>

          {isCaptain && (
            <div className="flex items-center gap-3 bg-primary/5 border border-primary/20 rounded-xl p-4 my-2">
              <div className="w-10 h-10 bg-primary/15 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="fa fa-crown text-primary" />
              </div>
              <div>
                <p className="font-bold text-gray-800 text-sm">
                  {t.dine_together_captain_title || "You're the Captain"}
                </p>
                <p className="text-gray-500 text-xs mt-0.5">
                  {t.dine_together_captain_desc ||
                    'Pick a package below to start the group session'}
                </p>
              </div>
            </div>
          )}

          {/* Page title */}
          <div className="px-4 pt-5 pb-3">
            <h2 className="text-xl font-bold text-gray-900">
              {t.dine_together_select_package_title || 'Select Package'}
            </h2>
          </div>

          {/* Packages */}
          <div className="px-4 space-y-4">
            {categories
              .filter((c) => !c.IsAddOn)
              .filter((c) => c.isAvailable)
              .map((cat) => {
                const isSelected = selectedPackage?._id === cat._id;
                const taggedSubs = subCategories.filter((s) =>
                  cat.TaggedCategories?.some((tc) => tc._id === s._id),
                );
                return (
                  <>
                    <div
                      key={cat._id}
                      className={`together-package-card border-2 rounded-2xl overflow-hidden ${isSelected ? 'selected border-primary' : 'border-gray-100 bg-white'}`}
                    >
                      {/* Package header image */}
                      <div className="relative h-32 bg-gray-100">
                        <img
                          src={cat.CoverImage || cat.cover || DEFAULT_IMG}
                          alt={cat.Category}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.src = DEFAULT_IMG;
                          }}
                        />
                        {isSelected && (
                          <div className="absolute top-2 right-2 bg-primary text-white text-xs font-bold px-3 py-1 rounded-full">
                            <i className="fa fa-check mr-1" />
                            {t.dine_together_package_selected || 'Selected'}
                          </div>
                        )}
                      </div>

                      <div className="p-4">
                        <div className="flex items-center justify-between mb-1">
                          <h3 className="font-bold text-gray-900 text-base">{cat.Category}</h3>
                          {cat.Price ? (
                            <span className="text-primary font-bold text-sm flex-shrink-0 ml-2">
                              {currency}{' '}
                              {isSelected ? cat.Price * (packageQuantity || 1) : cat.Price}
                            </span>
                          ) : null}
                        </div>
                        {cat.Description && (
                          <p className="text-gray-500 text-sm mb-3">{cat.Description}</p>
                        )}

                        {/* Included items */}
                        {/* TEMP: hidden the "{N}h Session" bullet so the package list
                          no longer reminds the user of the session time limit.
                          Restore the inner conditional below when the time-limit
                          copy is reinstated. */}
                        {taggedSubs.length > 0 && (
                          <ul className="together-package-includes mb-3">
                            {/*
                          {restaurant?.TableSessionTimeout && (
                            <li>
                              {restaurant.TableSessionTimeout}h{' '}
                              {t.dine_together_session_tab || 'Session'}
                            </li>
                          )}
                          */}
                            {taggedSubs.map((sub) => (
                              <li key={sub._id}>{sub.Category}</li>
                            ))}
                          </ul>
                        )}

                        {/* A la carte note */}
                        <div className="flex items-center gap-2 mt-2 mb-4">
                          <span className="together-alacarte-badge">
                            <i className="fa fa-plus-circle" />
                            {t.dine_together_alacarte_label || 'A La Carte available'}
                          </span>
                        </div>

                        <div className="pt-2">
                          <p className="text-sm font-semibold text-gray-800">
                            {t.dine_together_people_question || 'How many people are dining?'}
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {t.dine_together_people_hint ||
                              'Price is per person — if 3 people are dining, select 3.'}
                          </p>
                        </div>
                        <div className="flex items-center justify-between gap-3 py-2">
                          <span className="text-sm font-semibold text-gray-700">
                            {t.dine_together_quantity || 'Quantity'}
                          </span>
                          <Counter
                            value={isSelected ? packageQuantity : 0}
                            minCount={0}
                            className="!bg-transparent"
                            onChange={(next) => handlePackageQuantityChange(cat, next)}
                          />
                        </div>

                        {/* Add-ons — only those allowed for the selected main package */}
                        {isSelected &&
                          (() => {
                            const visibleAddOns = cat?.AddOnCategories;
                            if (!visibleAddOns?.length) return null;
                            return (
                              <div className="mt-4">
                                <h3 className="text-base font-bold text-gray-900 mb-1">
                                  {t.dine_together_addons_title || 'Add-ons'}
                                </h3>
                                <p className="text-xs text-gray-500 mb-2">
                                  {t.dine_together_addons_subtitle ||
                                    'Optional extras for your group.'}
                                </p>
                                <div className="space-y-2">
                                  {visibleAddOns.map((addOn) => (
                                    <div
                                      key={addOn._id}
                                      className="flex items-center justify-between bg-white border border-gray-200 rounded-xl p-2"
                                    >
                                      <div className="flex-1 min-w-0 pr-3">
                                        <p className="font-semibold text-gray-900 text-sm truncate">
                                          {addOn.Category}
                                        </p>
                                        {addOn.Price ? (
                                          <p className="text-xs text-primary font-semibold mt-0.5">
                                            {currency} {addOn.Price}
                                          </p>
                                        ) : null}
                                      </div>
                                      <Counter
                                        value={addOnQuantities[addOn._id] || 0}
                                        minCount={0}
                                        className="!bg-transparent"
                                        onChange={(next) =>
                                          setAddOnQuantities((q) => ({ ...q, [addOn._id]: next }))
                                        }
                                      />
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })()}
                      </div>
                    </div>
                  </>
                );
              })}
          </div>

          {/* Confirm & Start Session — fixed bottom */}
          <div className="fixed bottom-0 left-0 right-0 flex justify-center items-center bg-white border-t border-gray-100 px-4 py-2 shadow-lg z-40">
            <button
              onClick={handleConfirmPackage}
              disabled={!selectedPackage}
              className={`w-full max-w-lg mx-auto py-3 md:py-4 rounded-full font-bold text-sm md:text-base transition-colors ${selectedPackage ? 'bg-primary hover:bg-secondary text-white' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
            >
              {t.dine_together_confirm_start || 'Confirm & Start Session'}
            </button>
          </div>
        </div>
      )}

      {/* ── JOIN CONFIRM SCREEN (Companion) ──────────────────────────────────── */}
      {screen === 'join-confirm' && (
        <div className="together-page max-w-lg mx-auto pb-32">
          {/* Header */}
          <div className="relative overflow-hidden" style={{ height: 260 }}>
            <img
              src={restaurant.cover}
              alt={restaurant.name}
              className="w-full h-full object-cover"
            />
            <div
              className="absolute inset-0 flex flex-col justify-end p-4 pb-5"
              style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 60%)' }}
            >
              <div className="flex items-center gap-2 mb-1">
                <span className="bg-white/20 backdrop-blur-sm text-white text-xs font-semibold px-3 py-1 rounded-full border border-white/30">
                  <i className="fa fa-table mr-1" />
                  {t.dine_together_table_label || 'Table'} {tableNumber}
                </span>
                <span className="bg-primary text-white text-xs font-semibold px-3 py-1 rounded-full">
                  {t.dine_together_status_active || 'Active'}
                </span>
              </div>
              <h1 className="text-white text-2xl font-bold">{restaurant.name}</h1>
            </div>
          </div>

          {/* Page title */}
          <div className="px-4 pt-5 pb-3">
            <h2 className="text-xl font-bold text-gray-900">
              {t.dine_together_join_title || 'Join Session'}
            </h2>
          </div>

          {/* Information / Session tabs */}
          <div className="px-4 mb-4">
            <div className="together-tab-switcher">
              <ToggleNav
                options={[
                  { id: 'information', label: t.information || 'Information' },
                  { id: 'session', label: t.dine_together_session_tab || 'Session' },
                ]}
                activeId={packageTab}
                onChange={(tab) => setPackageTab(tab)}
              />
            </div>
          </div>

          {packageTab === 'information' ? (
            <div className="px-4">
              {/* Session started by */}
              <div className="flex items-center gap-3 bg-gray-50 border border-gray-100 rounded-xl p-4 mb-5">
                <div className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0 bg-primary flex items-center justify-center">
                  {session?.CreatedBy?.Avatar ? (
                    <img
                      src={session.CreatedBy.Avatar}
                      alt={session.CreatedBy.Name || ''}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-white text-sm font-bold">
                      {(session?.CreatedBy?.Name || '?').charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                <div>
                  <p className="text-xs text-gray-500">
                    {t.dine_together_session_started_by || 'Session started by'}
                  </p>
                  <p className="text-xs font-semibold text-gray-800">
                    {session?.CreatedBy?.Name || t.dine_together_captain || 'Captain'}
                  </p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-gray-500">
                      {memberCount} {t.dine_together_members || 'member(s)'}
                    </span>
                    {/* TEMP: hidden "Ends {time}" timer pill in the lobby header
                        so the user no longer sees the 2-hour time-limit reminder.
                        Restore the conditional span below when the time-limit
                        copy is reinstated. */}
                    {/*
                    {timerDisplay && (
                      <span className="text-xs text-orange-600 font-semibold">
                        <i className="fa fa-clock mr-1" />
                        {t.dine_together_ends_at || 'Ends'} {timerDisplay}
                      </span>
                    )}
                    */}
                  </div>
                </div>
              </div>

              {/* Locked package */}
              {session?.HigherCategory ? (
                <>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                    {t.dine_together_package_by_captain || 'Package selected by Captain'}
                  </p>
                  <div className="border-2 border-primary/30 rounded-2xl overflow-hidden bg-primary/5 mb-4">
                    <div className="relative h-28">
                      <img
                        src={
                          session.HigherCategory.CoverImage ||
                          session.HigherCategory.cover ||
                          DEFAULT_IMG
                        }
                        alt={session.HigherCategory.Category}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = DEFAULT_IMG;
                        }}
                      />
                      <div className="absolute top-2 left-2 bg-primary text-white text-xs font-bold px-3 py-1 rounded-full">
                        {t.dine_together_captains_choice || "Captain's Choice"}
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="font-bold text-gray-900 text-base">
                        {session.HigherCategory.Category}
                      </h3>
                      {session.HigherCategory.Description && (
                        <p className="text-gray-500 text-sm mt-1">
                          {session.HigherCategory.Description}
                        </p>
                      )}
                      {(() => {
                        const lockedCatFull = categoriesCopy.find(
                          (c) => c._id === session.HigherCategory._id,
                        );
                        const taggedSubs = subCategories.filter((s) =>
                          lockedCatFull?.TaggedCategories?.some((tc) => tc._id === s._id),
                        );
                        // TEMP: dropped restaurant?.TableSessionTimeout from the
                        // showList check + hid the "{N}h Session" bullet so the
                        // locked package card no longer reminds the user of the
                        // 2-hour time limit. Restore the OR clause and the
                        // conditional <li> below when the time-limit copy is
                        // reinstated.
                        const showList = taggedSubs.length > 0;
                        return showList ? (
                          <ul className="together-package-includes mt-3">
                            {/*
                            {restaurant?.TableSessionTimeout && (
                              <li>
                                {restaurant.TableSessionTimeout}h{' '}
                                {t.dine_together_session_tab || 'Session'}
                              </li>
                            )}
                            */}
                            {taggedSubs.map((sub) => (
                              <li key={sub._id}>{sub.Category}</li>
                            ))}
                          </ul>
                        ) : null;
                      })()}
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex items-center gap-3 bg-amber-50 border border-amber-100 rounded-xl p-4 mb-4">
                  <i className="fa fa-clock text-amber-500 text-lg flex-shrink-0" />
                  <p className="text-sm text-amber-700">
                    {t.dine_together_waiting_captain ||
                      'Waiting for the captain to select a package…'}
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="px-4">
              {/* Session details */}
              <div className="bg-white border border-gray-100 rounded-2xl p-5 space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">
                    {t.dine_together_table_label || 'Table'}
                  </span>
                  <span className="font-semibold text-gray-900">{tableNumber}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">
                    {t.dine_together_package_label || 'Package'}
                  </span>
                  <span className="font-semibold text-gray-900">
                    {session?.HigherCategory?.Category ||
                      t.dine_together_waiting_captain ||
                      'Pending'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">
                    {t.dine_together_members || 'Members'}
                  </span>
                  <span className="font-semibold text-gray-900">{memberCount}</span>
                </div>
                {/* TEMP: hidden "Session ends {time}" row in the session
                    details panel so the user no longer sees the 2-hour
                    time-limit reminder. Restore the conditional <div> below
                    when the time-limit copy is reinstated. */}
                {/*
                {timerDisplay && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">
                      {t.dine_together_session_ends || 'Session ends'}
                    </span>
                    <span className="font-semibold text-orange-600">{timerDisplay}</span>
                  </div>
                )}
                */}
              </div>
            </div>
          )}

          {/* Join Session fixed bottom */}
          <div className="fixed bottom-0 left-0 right-0 flex justify-center items-center bg-white border-t border-gray-100 px-4 py-2 shadow-lg z-40">
            <button
              onClick={handleJoinConfirm}
              className="w-full max-w-lg mx-auto py-3 md:py-4 rounded-full font-bold text-sm md:text-base bg-primary hover:bg-secondary text-white transition-colors flex items-center justify-center gap-2"
            >
              <i className="fa fa-users" />
              {isRejoin
                ? t.dine_together_rejoin_btn || 'Rejoin Session'
                : t.dine_together_join_btn || 'Join Session'}
            </button>
          </div>
        </div>
      )}

      {/* ── ACTIVE SESSION ───────────────────────────────────────────────────── */}
      {screen === 'active' && (
        <div className="together-page pb-[140px]">
          {/* Sticky session header */}
          <div className="together-session-header bg-primary sticky top-[48px] md:top-[51px] z-30 px-4 py-3">
            <div className="max-w-3xl mx-auto flex items-center justify-between gap-3">
              {/* Left: restaurant + table */}
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 border border-white/30">
                  <img src={restaurant.cover} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="min-w-0">
                  <p className="text-white font-bold text-sm truncate">{restaurant.name}</p>
                  <p className="text-white/70 text-xs">
                    <i className="fa fa-table mr-1" />
                    {t.dine_together_table_label || 'Table'} {tableNumber}
                  </p>
                </div>
              </div>

              {/* Right: timer + members + share */}
              <div className="flex items-center gap-2 flex-shrink-0">
                {/* TEMP: hidden "Ends {time}" pill in the active session sticky
                    header so the user no longer sees the 2-hour time-limit
                    reminder. Restore the conditional <div> below when the
                    time-limit copy is reinstated. */}
                {/*
                {timerDisplay && (
                  <div className="together-timer-badge px-2.5 py-1 rounded-full text-white text-xs font-semibold flex items-center gap-1">
                    <i className="fa fa-clock" />
                    {t.dine_together_ends_at || 'Ends'} {timerDisplay}
                  </div>
                )}
                */}
                <button
                  type="button"
                  onClick={() => setShowParticipantsModal(true)}
                  className="flex items-center hover:opacity-80 transition-opacity"
                  title={t.dine_together_view_members || 'View members'}
                >
                  {(session?.Participants || []).slice(0, 4).map((p) =>
                    p.Avatar ? (
                      <div
                        key={p._id}
                        className={`together-avatar${p?._id === (userProfile?.token ? userProfile?.profile?.userID : orderContext.guestId) ? ' you' : ''} overflow-hidden p-0`}
                      >
                        <img
                          src={p.Avatar}
                          alt={p.Name || ''}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <div key={p._id} className="together-avatar text-[10px] font-bold">
                        {(p.Name || '?').charAt(0).toUpperCase()}
                      </div>
                    ),
                  )}
                  {memberCount > 4 && (
                    <div className="together-avatar text-[9px]">+{memberCount - 4}</div>
                  )}
                </button>
                {/* Share button */}
                <button
                  onClick={() => setShowShareModal(true)}
                  className="together-timer-badge px-2.5 py-1 rounded-full text-white text-xs font-semibold flex items-center gap-1 hover:bg-white/25 transition-colors"
                  title={t.dine_together_share_btn || 'Share Table'}
                >
                  <i className="fa fa-qrcode" />
                  <span className="hidden sm:inline">
                    {t.dine_together_share_btn || 'Share Table'}
                  </span>
                </button>
                {/* Group Dine label */}
                <span className="bg-primary text-white text-xs font-bold uppercase tracking-wide hidden sm:block ml-1">
                  {t.dine_together_label || 'Group Dine'}
                </span>
              </div>
            </div>
          </div>

          {/* Orders | Menu tab switcher */}
          <div className="bg-white border-b border-gray-100 px-4">
            <div className="max-w-3xl mx-auto">
              <div className="together-tab-switcher min-w-[220px]">
                <ToggleNav
                  options={[
                    {
                      id: 'orders',
                      label: `${t.dine_together_orders_tab || 'Orders'}${sessionOrders.length > 0 ? ` (${sessionOrders.length})` : ''}`,
                    },
                    { id: 'menu', label: t.dine_together_menu_tab || 'Menu' },
                  ]}
                  activeId={activeTab}
                  onChange={(tab) => {
                    setActiveTab(tab);
                    if (tab === 'orders') fetchSessionOrders();
                  }}
                />
              </div>
            </div>
          </div>

          {/* Captain badge */}
          {isCaptain && (
            <div className="max-w-3xl mx-auto">
              <div className="flex justify-center items-center gap-2 bg-primary/5 border border-primary/20 rounded-xl p-3 my-2">
                <div className="w-6 h-6 bg-primary/15 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="fa fa-crown text-primary" />
                </div>
                <div>
                  <p className="font-bold text-gray-800 text-sm">
                    {t.dine_together_captain_title || "You're the Captain"}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* ── MENU TAB ── */}
          {activeTab === 'menu' && (
            <div className="max-w-3xl mx-auto px-4">
              {/* Locked category header */}
              {session?.HigherCategory && (
                <div className="pt-4 pb-2">
                  <div className="inline-flex items-center gap-2 bg-primary/10 rounded-full px-4 py-1.5">
                    <i className="fa fa-lock text-primary text-xs" />
                    <span className="text-primary font-semibold text-sm">
                      {session.HigherCategory.Category}
                    </span>
                  </div>
                </div>
              )}

              {/* Category tabs — only shown if multiple locked categories possible */}
              {!session?.HigherCategory && (
                <CategoryTabs
                  categories={categories}
                  selectedCategory={selectedCategory}
                  onSelectCategory={(cat) => {
                    const first = sessionMenuItems.find((g) => g.higherCategoryId === cat._id);
                    if (first) scrollTo(`${first.higherCategoryId}_${first.category}`, 180);
                  }}
                  fromKiosk
                />
              )}

              <div className="subcategories-header sticky top-[103px] sm:top-[95px] z-31">
                <SubCategories
                  ref={subNavRef}
                  subCategories={currentSubCategories}
                  activeSubCategory={activeSubCategory}
                  onSelectSubCategory={(sub) => {
                    const id = `${sub.higherCategoryId}_${sub.category || sub.Category || sub.name}`;
                    scrollTo(id, 180);
                  }}
                  hasHigherCategories={!!session?.HigherCategory}
                  fromKiosk
                />
              </div>

              <MenuGrid
                restaurant={restaurant}
                categories={categories}
                groupedItems={sessionMenuItems}
                defaultImg={DEFAULT_IMG}
                onSelectItem={handleItemSelectionModalOpen}
                activeSubCategory={activeSubCategory}
                hasHigherCategories={hasHigherCategories}
              />
            </div>
          )}

          {/* ── ORDERS TAB ── */}
          {activeTab === 'orders' && (
            <div className="max-w-3xl mx-auto px-4 py-4">
              {sessionOrders.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <i className="fa fa-receipt text-gray-300 text-2xl" />
                  </div>
                  <p className="text-gray-400 font-semibold text-sm mb-1">
                    {t.dine_together_no_orders_title || 'No orders yet'}
                  </p>
                  <p className="text-gray-400 text-sm">
                    {t.dine_together_no_orders_desc ||
                      'Your orders will appear here once submitted.'}
                  </p>
                  <button
                    onClick={() => setActiveTab('menu')}
                    className="mt-5 bg-primary text-white text-sm font-semibold px-6 py-2.5 rounded-full hover:bg-secondary transition-colors"
                  >
                    {t.dine_together_browse_menu || 'Browse Menu'}
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {[...sessionOrders].reverse().map((order) => {
                    const myUserId =
                      (userProfile?.profile?.userID ?? userProfile?._id)?.toString() || '';
                    const orderUserId = order.User?._id?.toString() || '';
                    const isMe = myUserId && orderUserId === myUserId;
                    const displayName = isMe
                      ? t.dine_together_you || 'You'
                      : order.User?.Name || 'Guest';
                    const avatarColors = [
                      'bg-primary',
                      'bg-teal-500',
                      'bg-amber-500',
                      'bg-purple-500',
                      'bg-rose-500',
                      'bg-blue-500',
                    ];
                    const colorIdx =
                      orderUserId.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0) %
                      avatarColors.length;
                    const avatarColor = isMe ? 'bg-primary' : avatarColors[colorIdx];
                    const statusMap = {
                      accepted: {
                        label: t.dine_together_order_confirmed || 'Confirmed',
                        cls: 'bg-green-100 text-green-700',
                      },
                      served: {
                        label: t.dine_together_order_served || 'Served',
                        cls: 'bg-blue-100 text-blue-700',
                      },
                    };
                    const statusInfo = statusMap[order.Status] || {
                      label: t.dine_together_order_pending || 'Sent to kitchen',
                      cls: 'bg-amber-50 text-amber-600',
                    };
                    return (
                      <div
                        key={order._id}
                        className="together-order-card bg-white rounded-2xl p-4 shadow-sm border border-gray-100"
                      >
                        <div className="flex items-center gap-3 mb-3">
                          <div
                            className={`w-8 h-8 rounded-full ${avatarColor} flex items-center justify-center text-white text-xs font-bold flex-shrink-0`}
                          >
                            {displayName.charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="text-sm font-semibold text-gray-800">
                              {displayName}{' '}
                              <span className="font-normal text-gray-400">
                                {t.dine_together_ordered || 'ordered'}
                              </span>
                            </span>
                            <span className="block text-xs text-gray-400">
                              {new Date(order.Date || order.CreatedAt).toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </span>
                          </div>
                          <span
                            className={`text-xs font-bold px-2.5 py-1 rounded-full flex-shrink-0 ${statusInfo.cls}`}
                          >
                            {statusInfo.label}
                          </span>
                        </div>
                        <div className="space-y-1.5 pl-11">
                          {(order.Order || []).map((item, idx) => (
                            <div key={idx} className="flex items-start justify-between gap-2">
                              <div className="flex items-start gap-2 min-w-0 flex-1">
                                <span className="text-gray-400 text-sm font-semibold flex-shrink-0">
                                  {item.qty}×
                                </span>
                                <div className="min-w-0 flex-1">
                                  <span className="block text-gray-800 text-sm truncate">
                                    {item.name}
                                  </span>
                                  {item.notes && (
                                    <span className="block text-gray-400 text-sm italic mt-0.5">
                                      {item.notes}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <span className="text-gray-500 text-sm flex-shrink-0">
                                {currency} {((item.price || 0) * (item.qty || 1)).toFixed(2)}
                              </span>
                            </div>
                          ))}
                          <div className="pt-2 border-t border-gray-100 flex justify-between mt-1">
                            <span className="text-sm font-semibold text-gray-700">
                              {t.total || 'Total'}
                            </span>
                            <span className="text-sm font-bold text-gray-900">
                              {currency} {(order.Total || 0).toFixed(2)}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── BOTTOM BAR ─────────────────────────────────────────────────── */}
          <div className="together-cart-bar space-y-1 md:space-y-2 py-2">
            {/* Cart summary row */}
            {orderItems.length > 0 && (
              <div className="max-w-3xl mx-auto px-4 flex items-center justify-between gap-3">
                <p className="text-sm font-semibold text-gray-600">
                  {orderItems.length} {t.item_length_title || 'item(s)'}
                  <span className="ml-2 text-gray-800">
                    {currency} {cartTotal.toFixed(2)}
                  </span>
                </p>
                <button
                  onClick={handlePlaceOrder}
                  disabled={isSubmittingOrder}
                  className={`flex items-center gap-2 bg-primary hover:bg-secondary text-white text-sm font-bold px-5 py-2.5 rounded-full transition-colors disabled:opacity-60 ${
                    !isSubmittingOrder ? 'together-send-btn' : ''
                  }`}
                >
                  {isSubmittingOrder ? (
                    <i className="fa fa-spinner fa-spin" />
                  ) : (
                    <i className="fa fa-paper-plane" />
                  )}
                  {t.dine_together_send_order || 'Send Order'}
                </button>
              </div>
            )}

            {/* Session control row */}
            <div className="max-w-3xl mx-auto px-4 flex items-center gap-3">
              <button
                onClick={() => setShowEndConfirm(true)}
                className="flex-1 flex items-center justify-center gap-2 border border-red-300 text-red-500 hover:bg-red-50 text-sm font-semibold py-3 rounded-full transition-colors"
              >
                <i className="fa fa-stop-circle" />
                {t.dine_together_end_session || 'End Session'}
              </button>

              <button
                onClick={() => setShowLeaveConfirm(true)}
                className="flex-1 flex items-center justify-center gap-2 border border-gray-200 text-gray-500 hover:bg-gray-50 text-sm font-semibold py-3 rounded-full transition-colors"
              >
                <i className="fa fa-sign-out-alt" />
                {t.dine_together_leave_session || 'Leave Session'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════ */}
      {/* MODALS                                                                 */}
      {/* ═══════════════════════════════════════════════════════════════════════ */}

      {/* ── HOW GROUP DINE WORKS (Mechanics) modal ────────────────────────────── */}
      {showMechanics && (
        <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center animate-fadeIn">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowMechanics(false)} />
          <div className="relative w-full max-w-lg bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl p-6 animate-slideIn z-10 max-h-[90vh] overflow-y-auto">
            {/* Close */}
            <button
              onClick={() => setShowMechanics(false)}
              className="absolute top-4 right-4 w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-gray-500 hover:bg-gray-200"
            >
              <i className="fa fa-times text-sm" />
            </button>

            {/* Icon */}
            <div className="flex justify-center mb-4">
              <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center">
                <i className="fa fa-users text-primary text-2xl" />
              </div>
            </div>

            <h2 className="text-xl font-bold text-gray-900 text-center mb-1">
              {t.dine_together_mechanics_title || 'How Group Dine Works'}
            </h2>
            <p className="text-gray-400 text-sm text-center mb-6">
              {t.dine_together_mechanics_subtitle ||
                'Everything you need to know before starting the session'}
            </p>

            {/* Steps */}
            <div className="space-y-5 mb-6">
              {[
                {
                  icon: '👑',
                  bg: '#fef3c7',
                  title: t.dine_together_mechanic_1_title || 'Captain Starts the Session',
                  // TEMP: trimmed the trailing "A 2-hour timer begins for the whole
                  // group." sentence to remove the 2-hour time-limit reminder.
                  // Original fallback:
                  //   'The Captain picks a package and confirms. A 2-hour timer begins for the whole group.'
                  desc:
                    t.dine_together_mechanic_1_desc || 'The Captain picks a package and confirms.',
                },
                {
                  icon: '📱',
                  bg: '#dbeafe',
                  title: t.dine_together_mechanic_2_title || 'Everyone Orders Independently',
                  desc:
                    t.dine_together_mechanic_2_desc ||
                    'Each member browses the menu and places orders from their own phone — no waiting to combine.',
                },
                {
                  icon: '🔔',
                  bg: '#dcfce7',
                  title: t.dine_together_mechanic_3_title || 'Food Comes Straight to the Table',
                  desc:
                    t.dine_together_mechanic_3_desc ||
                    "Orders are sent directly to the kitchen as placed. Food arrives as it's ready.",
                },
                {
                  icon: '💳',
                  bg: '#f3e8ff',
                  title: t.dine_together_mechanic_4_title || 'Bill Settled at the End',
                  desc:
                    t.dine_together_mechanic_4_desc ||
                    'When the session ends, the restaurant tallies the bill. Payment is handled at the venue.',
                },
              ].map((step, i) => (
                <div key={i} className="together-mechanics-step">
                  <div className="together-mechanics-icon" style={{ background: step.bg }}>
                    <span>{step.icon}</span>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800 text-sm">{step.title}</p>
                    <p className="text-gray-400 text-sm mt-0.5">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA */}
            <button
              onClick={
                isCaptain
                  ? isRejoin
                    ? handleJoinComplete
                    : handleStartSession
                  : handleJoinComplete
              }
              disabled={isStartingSession}
              className="w-full py-3 md:py-4 bg-primary hover:bg-secondary text-white font-bold text-sm md:text-base rounded-full transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {isStartingSession ? (
                <i className="fa fa-spinner fa-spin" />
              ) : isCaptain ? (
                <>
                  {isRejoin ? (
                    <>
                      <i className="fa fa-users" /> {t.dine_together_rejoin_btn || 'Rejoin Session'}
                    </>
                  ) : (
                    <>
                      <i className="fa fa-play-circle" />{' '}
                      {t.dine_together_start_session || 'Start Session'}
                    </>
                  )}
                </>
              ) : (
                <>
                  <i className="fa fa-users" /> {t.dine_together_join_btn || 'Join Session'}
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* ── SHARE / QR CODE MODAL ────────────────────────────────────────────── */}
      {showShareModal && (
        <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center animate-fadeIn">
          <div
            className="fixed inset-0 bg-black/50"
            onClick={() => {
              setShowShareModal(false);
              setShareLinkCopied(false);
            }}
          />
          <div className="relative w-full max-w-sm bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl p-6 animate-slideIn z-10">
            {/* Close */}
            <button
              onClick={() => {
                setShowShareModal(false);
                setShareLinkCopied(false);
              }}
              className="absolute top-4 right-4 w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-gray-500 hover:bg-gray-200"
            >
              <i className="fa fa-times text-sm" />
            </button>

            {/* Header */}
            <div className="flex justify-center mb-4">
              <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center">
                <i className="fa fa-qrcode text-primary text-2xl" />
              </div>
            </div>
            <h2 className="text-xl font-bold text-gray-900 text-center mb-1">
              {t.dine_together_share_title || 'Invite Friends to Your Table'}
            </h2>
            <p className="text-gray-400 text-sm text-center mb-5">
              {t.dine_together_share_desc ||
                'Share this QR code or link so friends can join your group session.'}
            </p>

            {/* Table badge */}
            <div className="flex justify-center mb-4">
              <span className="inline-flex items-center gap-1.5 bg-gray-100 text-gray-600 text-xs font-semibold px-3 py-1.5 rounded-full">
                <i className="fa fa-table" />
                {restaurant?.name} · {t.dine_together_table_label || 'Table'} {tableNumber}
              </span>
            </div>

            {/* QR Code */}
            <div className="flex justify-center mb-4">
              <div className="bg-white border-2 border-gray-100 rounded-2xl p-3 shadow-sm">
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&margin=4&data=${encodeURIComponent(`${window.location.origin}/restaurant-together/${restaurantId}/${encodeURIComponent(tableNumber)}`)}`}
                  alt={t.dine_together_share_scan || 'Scan to join'}
                  width={200}
                  height={200}
                  className="rounded-lg"
                />
              </div>
            </div>
            <p className="text-xs text-gray-400 text-center mb-4">
              {t.dine_together_share_scan || 'Scan to join'}
            </p>

            {/* Copy link */}
            <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 mb-4">
              <span className="text-xs text-gray-500 truncate flex-1 font-mono select-all">
                {`${window.location.origin}/restaurant-together/${restaurantId}/${encodeURIComponent(tableNumber)}`}
              </span>
              <button
                onClick={() => {
                  const url = `${window.location.origin}/restaurant-together/${restaurantId}/${encodeURIComponent(tableNumber)}`;
                  navigator.clipboard?.writeText(url).then(() => {
                    setShareLinkCopied(true);
                    setTimeout(() => setShareLinkCopied(false), 2500);
                  });
                }}
                className="flex-shrink-0 flex items-center gap-1 bg-primary hover:bg-secondary text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors"
              >
                <i className={`fa ${shareLinkCopied ? 'fa-check' : 'fa-copy'}`} />
                {shareLinkCopied
                  ? t.dine_together_share_copied || 'Copied!'
                  : t.dine_together_share_copy || 'Copy Link'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── 25-minute Reminder ────────────────────────────────────────────────── */}
      {showReminder && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 animate-fadeIn">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowReminder(false)} />
          <div className="relative w-full max-w-sm bg-white rounded-2xl shadow-2xl p-6 z-10 animate-fadeIn">
            <div className="flex justify-center mb-4">
              <div className="w-14 h-14 bg-amber-50 rounded-full flex items-center justify-center">
                <i className="fa fa-clock text-amber-400 text-2xl" />
              </div>
            </div>
            <h3 className="text-lg font-bold text-gray-900 text-center mb-2">
              {t.dine_together_reminder_title || '25 minutes left'}
            </h3>
            <p className="text-gray-500 text-sm text-center mb-6">
              {t.dine_together_reminder_desc ||
                'Your session is ending soon. Make sure to place any final orders now.'}
            </p>
            <button
              onClick={() => setShowReminder(false)}
              className="w-full py-3 bg-primary hover:bg-secondary text-white font-semibold rounded-full transition-colors text-sm"
            >
              {t.dine_together_reminder_btn || 'Got it'}
            </button>
          </div>
        </div>
      )}

      {/* ── PARTICIPANTS LIST MODAL ──────────────────────────────────────────── */}
      {showParticipantsModal &&
        (() => {
          const captainId = (session?.CreatedBy?._id ?? session?.CreatedBy)?.toString();
          const currentUserId = userProfile?.token
            ? userProfile?.profile?.userID
            : orderContext.guestId;
          const participants = session?.Participants || [];

          return (
            <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 animate-fadeIn">
              <div
                className="fixed inset-0 bg-black/50"
                onClick={() => setShowParticipantsModal(false)}
              />
              <div className="relative w-full max-w-sm bg-white rounded-2xl shadow-2xl z-10 animate-fadeIn max-h-[80vh] flex flex-col">
                <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-gray-100">
                  <div>
                    <h3 className="text-base font-bold text-gray-900">
                      {t.dine_together_members_title || 'Members'}
                    </h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {participants.length} {t.dine_together_members || 'member(s)'}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowParticipantsModal(false)}
                    className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600"
                    aria-label={t.close || 'Close'}
                  >
                    <i className="fa fa-times" />
                  </button>
                </div>

                <ul className="overflow-y-auto px-2 py-2">
                  {participants.map((p) => {
                    const isParticipantCaptain = !!captainId && captainId === p._id?.toString();
                    const isCurrentUser = p._id === currentUserId;
                    return (
                      <li
                        key={p._id}
                        className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-gray-50"
                      >
                        <div className="w-10 h-10 rounded-full overflow-hidden bg-primary flex items-center justify-center flex-shrink-0">
                          {p.Avatar ? (
                            <img
                              src={p.Avatar}
                              alt={p.Name || ''}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span className="text-white text-sm font-bold">
                              {(p.Name || '?').charAt(0).toUpperCase()}
                            </span>
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <p className="font-semibold text-sm text-gray-900 truncate">
                              {p.Name || t.dine_together_guest_name || 'Guest'}
                            </p>
                            {isCurrentUser && (
                              <span className="inline-flex items-center bg-primary/10 text-primary border border-primary/30 text-[10px] font-semibold px-2 py-0.5 rounded-full">
                                {t.dine_together_you || 'You'}
                              </span>
                            )}
                          </div>
                          {p.Email && !p?.Name.includes('Guest') && (
                            <p className="text-xs text-gray-500 truncate">{p.Email}</p>
                          )}
                        </div>
                        {isParticipantCaptain ? (
                          <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-700 border border-amber-200 text-[10px] font-semibold px-2 py-1 rounded-full flex-shrink-0">
                            <i className="fa fa-crown" />
                            {t.dine_together_captain || 'Captain'}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 bg-gray-50 text-gray-600 border border-gray-200 text-[10px] font-semibold px-2 py-1 rounded-full flex-shrink-0">
                            <i className="fa fa-user" />
                            {t.dine_together_member || 'Member'}
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          );
        })()}

      {/* ── LEAVE SESSION CONFIRM ─────────────────────────────────────────────── */}
      {showLeaveConfirm && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 animate-fadeIn">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowLeaveConfirm(false)} />
          <div className="relative w-full max-w-sm bg-white rounded-2xl shadow-2xl p-6 z-10 animate-fadeIn">
            <div className="flex justify-center mb-4">
              <div className="w-14 h-14 bg-amber-50 rounded-full flex items-center justify-center">
                <i className="fa fa-sign-out-alt text-amber-400 text-2xl" />
              </div>
            </div>
            <h3 className="text-lg font-bold text-gray-900 text-center mb-2">
              {t.dine_together_leave_confirm_title || 'Leave the session?'}
            </h3>
            <p className="text-gray-500 text-sm text-center mb-6">
              {t.dine_together_leave_confirm_desc ||
                'You will be removed from this session. Other members will continue without you.'}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowLeaveConfirm(false)}
                className="flex-1 py-3 border border-gray-200 text-gray-600 font-semibold rounded-full hover:bg-gray-50 transition-colors text-sm"
              >
                {t.cancel || 'Cancel'}
              </button>
              <button
                onClick={handleLeaveSession}
                disabled={isLeavingSession}
                className="flex-1 py-3 bg-primary hover:bg-secondary text-white font-semibold rounded-full transition-colors text-sm disabled:opacity-60"
              >
                {isLeavingSession ? (
                  <i className="fa fa-spinner fa-spin" />
                ) : (
                  t.dine_together_leave_confirm_btn || 'Yes, leave'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── END SESSION CONFIRM ───────────────────────────────────────────────── */}
      {showEndConfirm && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 animate-fadeIn">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowEndConfirm(false)} />
          <div className="relative w-full max-w-sm bg-white rounded-2xl shadow-2xl p-6 z-10 animate-fadeIn">
            <div className="flex justify-center mb-4">
              <div className="w-14 h-14 bg-red-50 rounded-full flex items-center justify-center">
                <i className="fa fa-stop-circle text-red-400 text-2xl" />
              </div>
            </div>
            <h3 className="text-lg font-bold text-gray-900 text-center mb-2">
              {t.dine_together_end_confirm_title || 'End the session?'}
            </h3>
            <p className="text-gray-500 text-sm text-center mb-6">
              {t.dine_together_end_confirm_desc ||
                'This will end the session for everyone at the table. No more orders can be placed after this.'}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowEndConfirm(false)}
                className="flex-1 py-3 border border-gray-200 text-gray-600 font-semibold rounded-full hover:bg-gray-50 transition-colors text-sm"
              >
                {t.cancel || 'Cancel'}
              </button>
              <button
                onClick={handleEndSession}
                disabled={isEndingSession || endCountdown > 0}
                className="flex-1 py-3 bg-primary hover:bg-secondary text-white font-semibold rounded-full transition-colors text-sm disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isEndingSession ? (
                  <i className="fa fa-spinner fa-spin" />
                ) : endCountdown > 0 ? (
                  `${t.dine_together_end_confirm_btn || 'Yes, end it'} (${endCountdown}s)`
                ) : (
                  t.dine_together_end_confirm_btn || 'Yes, end it'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── ITEM SELECTION MODAL ─────────────────────────────────────────────── */}
      {selectedItem && (
        <Modal
          isOpen={isItemSelectionModalOpen}
          onClose={() => setIsItemSelectionModalOpen(false)}
          headerImg={selectedItem?.image || DEFAULT_IMG || restaurant?.MenuPlaceholder}
          FooterComponent={ItemSelectionFooter}
          noPadding
        >
          <div className="flex flex-col">
            <div className="p-4 space-y-2 sm:space-y-4 border-b border-gray-100">
              <h1 className="text-lg sm:text-xl font-semibold">{selectedItem?.name}</h1>
              <p className="text-sm text-gray-500 whitespace-pre-wrap">
                {selectedItem?.description}
              </p>
              <h2 className="text-sm font-semibold">
                {currency} {selectedItem?.price}
              </h2>
            </div>

            {extrasItems.length > 0 && (
              <div className="p-4 border-b border-gray-100">
                <h2 className="text-base font-semibold mb-4">{t.restaurant_menu_options}</h2>
                <MultiCounter
                  items={extrasItems}
                  onChange={handleExtrasChange}
                  currency={currency}
                  selectedValues={selectedExtras}
                  totalSelectedCount={0}
                />
              </div>
            )}

            {groupedAttributes.map((attribute) => {
              const attrSel = selectedAttributes.find(
                (a) => a.attributeId?.toString() === attribute._id?.toString(),
              );
              const selOpts = attrSel ? attrSel.options : [];
              const isRequired = attribute.attributesRequired;
              const connectedAttrs =
                selectedItem?.attributes
                  ?.filter((a) => {
                    if (!a.belongsto) return false;
                    return selOpts.some((sel) => {
                      if (!sel?.isconnectedto || !Array.isArray(sel?.isconnectedto)) return false;
                      return sel.isconnectedto.some((cid) => String(cid) === String(a._id));
                    });
                  })
                  .map((a) => ({ ...a, id: a._id })) || [];

              return (
                <div key={attribute._id} className="p-4 border-b border-gray-100">
                  <h2 className="text-base font-semibold mb-2">
                    {attribute.name}
                    {isRequired && (
                      <span className="text-sm text-red-500 ml-1">
                        &#40;{t.restaurant_menu_required}&#41;
                      </span>
                    )}
                  </h2>
                  {attribute.isMultiple ? (
                    <MultiCounter
                      items={(attribute.options || []).map((opt) => ({
                        _id: opt.id,
                        id: opt.id,
                        label: opt.name,
                        price: opt.additionalPrice,
                        initialCount: 0,
                        additionalPrice: opt.additionalPrice,
                        limit: opt.limit || null,
                        minlimit: opt.minlimit || null,
                        isconnectedto: opt?.isconnectedto || null,
                      }))}
                      onChange={(newCounts, itemId) =>
                        handleMultipleCounterChange(attribute._id, itemId, newCounts)
                      }
                      currency={currency}
                      maxOptionCount={attribute.limitPerCategory}
                      selectedValues={selOpts}
                      attribute={attribute}
                      totalSelectedCount={selectedAttributes?.reduce?.(
                        (total, a) =>
                          total + a?.options?.reduce?.((s, o) => s + (o?.quantity || 1), 0),
                        0,
                      )}
                      connectedAttributes={connectedAttrs}
                      onConnectedAttributeChange={handleRadioAttributeChange}
                      selectedAttributes={selectedAttributes}
                    />
                  ) : (
                    <RadioGroup
                      options={attribute.options}
                      onChange={(opt) => handleRadioAttributeChange(attribute._id, opt)}
                      currency={currency}
                      required={isRequired}
                      selectedOption={selOpts.length > 0 ? selOpts[0] : null}
                      connectedAttributes={connectedAttrs}
                      onConnectedAttributeChange={handleRadioAttributeChange}
                      selectedAttributes={selectedAttributes}
                    />
                  )}
                </div>
              );
            })}

            <div className="p-4 border-b border-gray-100">
              <h2 className="text-base font-semibold mb-4">{t.comments}</h2>
              <textarea
                className="w-full p-3 sm:p-4 bg-gray-50 rounded-xl h-[120px] resize-none text-sm"
                placeholder={`${t.comments_placeholder}...`}
                value={itemComments}
                onChange={(e) => setItemComments(e.target.value)}
              />
            </div>
          </div>
        </Modal>
      )}

      {/* ── VALIDATION ERROR ─────────────────────────────────────────────────── */}
      <ValidationErrorModal
        isOpen={isValidationErrorModalOpen}
        onClose={() => {
          setIsValidationErrorModalOpen(false);
          setValidationErrorMessage('');
        }}
        validationErrorMessage={validationErrorMessage}
      />
    </div>
  );
}
